import { Close, Description, Edit, Lightbulb, Send } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type React from "react";
import TextareaAutosize from "react-textarea-autosize";
import type { Document } from "../../../interface/document";
import type { Knowledge } from "../../../interface/knowledge";
import { useAttachmentHover } from "../hooks/useAttachmentHover";
import styles from "../styles.module.scss";
import { isNonEditableDocumentType } from "../types/documentType";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  isStreaming?: boolean;
  hasError?: boolean;
  onKnowledgeAttach?: () => void;
  onKnowledgeGenerate?: () => void;
  knowledgeGenerateDisabled?: boolean;
  knowledgeGenerateTooltip?: string;
  attachedKnowledge?: Knowledge[];
  attachedGeneratedDocuments?: Document[];
  onRemoveGeneratedDocument?: (documentId: string) => void;
  emphasizeSend?: boolean;
  sendCta?: string;
  editMode?: boolean;
  onEditModeChange?: (enabled: boolean) => void;
  documentType?: string;
}

// 文字数制限の定数
const MAX_CHARACTERS = 4000;

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = "メッセージを入力",
  disabled = false,
  isStreaming = false,
  hasError = false,
  onKnowledgeGenerate,
  knowledgeGenerateDisabled = false,
  knowledgeGenerateTooltip = "ナレッジを生成",
  attachedKnowledge = [],
  attachedGeneratedDocuments = [],
  onRemoveGeneratedDocument,
  emphasizeSend = false,
  sendCta,
  editMode = false,
  onEditModeChange,
  documentType,
}) => {
  // 編集モードが無効化されるドキュメントタイプをチェック
  const isEditModeDisabled = documentType ? isNonEditableDocumentType(documentType) : false;

  // CSS Modulesのクラス名を定数として定義（型安全性を確保）
  // インデックスアクセスは string | undefined を返す可能性があるため、型ガードで確認
  const attachmentIconVisible = styles["attachmentIconVisible"];
  const attachmentIconHidden = styles["attachmentIconHidden"];

  // クラス名が存在しない場合はエラーを投げる（実行時エラーを防ぐ）
  if (!attachmentIconVisible || !attachmentIconHidden) {
    throw new Error(
      "Required CSS classes are missing: attachmentIconVisible or attachmentIconHidden",
    );
  }

  const {
    handleMouseEnter: handleAttachmentMouseEnter,
    handleMouseLeave: handleAttachmentMouseLeave,
  } = useAttachmentHover({
    attachmentIconVisible,
    attachmentIconHidden,
  });

  // 文字数制限チェック
  const isOverLimit = value.length > MAX_CHARACTERS;

  // 送信ボタンの無効化条件を強化
  const isSendDisabled = !value.trim() || disabled || isOverLimit || isStreaming;

  // エラー状態のプレースホルダー
  const errorPlaceholder = hasError ? "エラーが発生しました。再送信してください。" : placeholder;

  // 入力値の変更処理（文字数制限なし）
  const handleValueChange = (newValue: string) => {
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME変換確定中は送信・改行処理を行わない
    if (e.nativeEvent.isComposing) return;

    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Shift+Enter: 明示的に改行を挿入（環境依存の挙動差を吸収）
        e.preventDefault();
        const textarea = e.currentTarget;
        const start = textarea.selectionStart ?? value.length;
        const end = textarea.selectionEnd ?? value.length;
        const newValue = `${value.slice(0, start)}\n${value.slice(end)}`;
        onChange(newValue);
        // キャレット位置を更新（高さはAutosizeに任せる）
        requestAnimationFrame(() => {
          textarea.selectionStart = start + 1;
          textarea.selectionEnd = start + 1;
        });
      } else {
        // Enterのみ: 送信
        e.preventDefault();
        if (value.trim() && !isSendDisabled) {
          onSend();
        }
      }
    }
  };

  /**
   * 【ドキュメント名省略表示】: 7文字まで表示し、以降は...で省略
   */
  const truncateDocumentName = (name: string, maxLength: number = 7): string => {
    if (name.length <= maxLength) {
      return name;
    }
    return `${name.substring(0, maxLength)}...`;
  };

  /**
   * 【ドキュメント削除処理】: 指定されたドキュメントIDのドキュメントを削除
   */
  const handleRemoveDocument = (documentId: string) => {
    onRemoveGeneratedDocument?.(documentId);
  };

  return (
    <div className={styles["chatInputWrapper"]}>
      {(attachedKnowledge.length > 0 || attachedGeneratedDocuments.length > 0) && (
        <div className={styles["attachmentsPreview"]}>
          {attachedKnowledge.map((_knowledge) => (
            <div key={_knowledge.id} className={styles["attachmentItem"]}>
              <Lightbulb fontSize="small" />
              <span>ナレッジ</span>
            </div>
          ))}
          {attachedGeneratedDocuments.map((doc) => (
            <Tooltip key={doc.id} title={doc.name} placement="top">
              <div
                className={styles["attachmentItem"]}
                data-testid={`attachment-item-${doc.id}`}
                onMouseEnter={(e) =>
                  handleAttachmentMouseEnter(
                    `delete-document-${doc.id}`,
                    `normal-icon-${doc.id}`,
                    e.currentTarget,
                  )
                }
                onMouseLeave={(e) =>
                  handleAttachmentMouseLeave(
                    `delete-document-${doc.id}`,
                    `normal-icon-${doc.id}`,
                    e.currentTarget,
                  )
                }
              >
                <Description
                  fontSize="small"
                  data-testid={`normal-icon-${doc.id}`}
                  className={attachmentIconVisible}
                />
                <Close
                  fontSize="small"
                  data-testid={`delete-document-${doc.id}`}
                  className={attachmentIconHidden}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRemoveDocument(doc.id)}
                />
                <span>{truncateDocumentName(doc.name)}</span>
              </div>
            </Tooltip>
          ))}
        </div>
      )}

      <div className={styles["chatInputContainer"]}>
        <div className={styles["inputInner"]}>
          <TextareaAutosize
            className={styles["textarea"]}
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={errorPlaceholder}
            disabled={disabled}
            minRows={1}
            maxRows={3}
            data-testid="text-input"
          />
        </div>

        <div className={styles["inputFooter"]}>
          <div className={styles["attachmentButtons"]}>
            <Tooltip title={knowledgeGenerateTooltip} placement="top">
              <span>
                <button
                  className={styles["attachButton"]}
                  onClick={onKnowledgeGenerate}
                  type="button"
                  aria-label="ナレッジ生成"
                  disabled={disabled || knowledgeGenerateDisabled}
                >
                  <Lightbulb />
                </button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                isEditModeDisabled
                  ? "このドキュメントで編集モードは使用できません"
                  : editMode
                    ? "編集モード: ON"
                    : "編集モード: OFF"
              }
              placement="top"
            >
              <span>
                <button
                  className={`${styles["attachButton"]} ${editMode ? styles["editModeActive"] : ""}`}
                  onClick={() => onEditModeChange?.(!editMode)}
                  type="button"
                  aria-label="編集モード"
                  disabled={disabled || isEditModeDisabled}
                >
                  <Edit />
                </button>
              </span>
            </Tooltip>
          </div>
          {emphasizeSend && sendCta && !isSendDisabled && (
            <span className={styles["sendCta"]}>{sendCta}</span>
          )}
          <Tooltip title={isSendDisabled ? "メッセージを入力してください" : "送信"} placement="top">
            <span>
              <button
                className={`${styles["sendButton"]} ${emphasizeSend ? styles["sendButtonEmphasis"] : ""}`}
                onClick={onSend}
                disabled={isSendDisabled}
                aria-label="送信"
                type="button"
                data-testid="generate-button"
              >
                <Send />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
